import { useState, useEffect, useCallback } from 'react';
import '../types/klever.d.ts';

// Session 1 hotfix (2026-05-25): the previous useKleverWallet pattern relied
// on `connect()` being called by EVERY useKleverWallet instance's auto-
// reconnect effect (3+ instances per page: header, footer, App body, …).
// When we switched to silent restore via getWalletAddress(), only one
// instance — the one the user clicked Connect in — would update its state.
// Other instances stayed disconnected forever, breaking the "Proceed to
// Payment" button (App.tsx body's isConnected was false even though the
// header showed connected). Fix: a small custom-event pub/sub. connect()
// and disconnect() dispatch KLEVER_WALLET_EVENT; every instance's useEffect
// listens and re-syncs from localStorage + the extension.
const KLEVER_WALLET_EVENT = 'klever-wallet-state-change';

interface WalletState {
  isConnected: boolean;
  isConnecting: boolean;
  address: string | null;
  error: string | null;
}

interface UseKleverWalletReturn extends WalletState {
  connect: () => Promise<void>;
  disconnect: () => void;
  sendPayment: (receiver: string, amount: number, token: string, memo?: string) => Promise<string>;
  isExtensionAvailable: boolean;
}

export function useKleverWallet(): UseKleverWalletReturn {
  const [state, setState] = useState<WalletState>({
    isConnected: false,
    isConnecting: false,
    address: null,
    error: null,
  });

  const [isExtensionAvailable, setIsExtensionAvailable] = useState(false);

  // Check if Klever extension is available
  useEffect(() => {
    const checkExtension = () => {
      const available = typeof window !== 'undefined' && !!window.kleverWeb;
      setIsExtensionAvailable(available);
    };

    // Check immediately
    checkExtension();

    // Also check after a delay (extension might load late)
    const timer = setTimeout(checkExtension, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Connect to wallet
  const connect = useCallback(async () => {
    if (!window.kleverWeb) {
      setState(prev => ({
        ...prev,
        error: 'Klever Extension not found. Please install it from klever.io',
      }));
      return;
    }

    setState(prev => ({ ...prev, isConnecting: true, error: null }));

    try {
      await window.kleverWeb.initialize();
      const address = await window.kleverWeb.getWalletAddress();

      setState({
        isConnected: true,
        isConnecting: false,
        address,
        error: null,
      });

      // Session 1 (2026-05-25, closes PT-P2-8): persist the WALLET ADDRESS,
      // not just a "connected" boolean. Auto-reconnect verifies the
      // currently-loaded extension wallet matches before silently
      // reconnecting. Without this, a different user on the same browser
      // (or the same user switching wallets in the extension) silently
      // gets logged in as whichever wallet the extension currently exposes.
      localStorage.setItem('klever_connected_wallet', address);
      // Session 1 hotfix: notify other useKleverWallet instances mounted
      // elsewhere on the page (App.tsx body, footer, etc.) so their state
      // syncs without requiring a remount. See KLEVER_WALLET_EVENT listener
      // below for the receiver.
      window.dispatchEvent(new CustomEvent(KLEVER_WALLET_EVENT));
    } catch (error) {
      setState(prev => ({
        ...prev,
        isConnecting: false,
        error: error instanceof Error ? error.message : 'Failed to connect wallet',
      }));
    }
  }, []);

  // Disconnect wallet
  const disconnect = useCallback(() => {
    setState({
      isConnected: false,
      isConnecting: false,
      address: null,
      error: null,
    });
    localStorage.removeItem('klever_connected_wallet');
    localStorage.removeItem('klever_connected'); // legacy key cleanup
    window.dispatchEvent(new CustomEvent(KLEVER_WALLET_EVENT));
  }, []);

  // Send payment using the 3-step Klever API: build -> sign -> broadcast
  const sendPayment = useCallback(
    async (receiver: string, amount: number, token: string, memo?: string): Promise<string> => {
      if (!window.kleverWeb || !state.isConnected) {
        throw new Error('Wallet not connected');
      }

      try {
        // For KDA tokens (USDC, USDT), we need to specify the token ID
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload: any = {
          receiver,
          amount: Math.round(amount * 1_000_000), // Convert to smallest unit (6 decimals)
          kda: token, // Token ID like "USDC-1LN4"
        };

        // Add memo/data if provided (as array of strings)
        if (memo) {
          payload.data = [memo];
        }

        // Step 1: Build unsigned transaction
        // TransactionType.Transfer = 0
        const unsignedTx = await window.kleverWeb.buildTransaction([
          { payload, type: 0 }
        ]);

        // Step 2: Sign the transaction
        const signedTx = await window.kleverWeb.signTransaction(unsignedTx);

        // Step 3: Broadcast the transaction
        const response = await window.kleverWeb.broadcastTransactions([signedTx]);

        // Return the transaction hash
        if (response && response.data && response.data.txsHashes && response.data.txsHashes.length > 0) {
          return response.data.txsHashes[0];
        }

        throw new Error('No transaction hash returned');
      } catch (error) {
        throw error instanceof Error ? error : new Error('Transaction failed');
      }
    },
    [state.isConnected]
  );

  // Auto-reconnect if previously connected.
  //
  // Session 1 (2026-05-25, closes PT-P2-8 + PT-P2-9): only auto-reconnect
  // if the extension's currently-exposed wallet matches the stored one.
  // Previously this called connect() on every page load whenever
  // `klever_connected === 'true'`, which (a) silently logged in as a
  // different wallet if the user switched wallets in the extension, and
  // (b) popped an unsolicited extension permission/signature dialog on
  // every page load. The new check queries the extension's address
  // first; if it matches, sets state directly (no popup); if it mismatches
  // or the extension is locked, the stored wallet is cleared and the user
  // must click Connect explicitly.
  useEffect(() => {
    // Session 1 hotfix (2026-05-25): migrate the legacy `klever_connected`
    // boolean key to the new `klever_connected_wallet` schema. Users who
    // connected on pre-1.2.0 portal have `klever_connected: 'true'` but
    // not the new key. Without migration, our auto-reconnect early-returns
    // and they must click Connect explicitly — which leaves App.tsx's
    // useKleverWallet instance disconnected (multi-instance state divergence
    // pre-event-bus). Migration: if legacy key is present and new key isn't,
    // and the extension currently exposes an address, write it.
    if (
      isExtensionAvailable &&
      localStorage.getItem('klever_connected') === 'true' &&
      !localStorage.getItem('klever_connected_wallet')
    ) {
      (async () => {
        try {
          const addr = await window.kleverWeb?.getWalletAddress();
          if (addr) {
            localStorage.setItem('klever_connected_wallet', addr);
            localStorage.removeItem('klever_connected');
            window.dispatchEvent(new CustomEvent(KLEVER_WALLET_EVENT));
          }
        } catch {
          // Extension locked — leave legacy key in place; user will see
          // disconnected state and click Connect.
        }
      })();
    }

    const syncFromStorage = async (): Promise<void> => {
      const storedWallet = localStorage.getItem('klever_connected_wallet');

      if (!storedWallet) {
        // Disconnected elsewhere (or never connected). If our local state
        // says connected, reset.
        if (state.isConnected) {
          setState({ isConnected: false, isConnecting: false, address: null, error: null });
        }
        return;
      }

      if (!isExtensionAvailable || state.isConnected) {
        return;
      }

      try {
        // Query the address the extension currently exposes. If the
        // extension is unlocked AND the user previously authorised this
        // origin, this returns the address without a popup. If locked or
        // unauthorised, it throws — we fall through to the catch and
        // leave stored binding intact.
        const currentAddress = await window.kleverWeb?.getWalletAddress();
        if (currentAddress && currentAddress === storedWallet) {
          // Same wallet — restore state without calling initialize() and
          // therefore without popping any extension dialog.
          setState({
            isConnected: true,
            isConnecting: false,
            address: currentAddress,
            error: null,
          });
        } else if (currentAddress && currentAddress !== storedWallet) {
          // Extension exposes a DIFFERENT wallet now (user switched
          // accounts in the extension). Clear stored binding; user must
          // explicitly click Connect to authorise the new wallet.
          localStorage.removeItem('klever_connected_wallet');
        }
      } catch {
        // Extension locked or query failed — leave stored binding alone.
      }
    };

    // Initial sync on mount + whenever extension availability flips.
    void syncFromStorage();

    // Session 1 hotfix: pub/sub so all useKleverWallet instances on the
    // page stay in sync when ANY of them connects/disconnects. Without
    // this, the multi-instance state divergence breaks the "Proceed to
    // Payment" gate (App.tsx body's isConnected stays false even though
    // the header WalletConnect shows connected).
    const handler = () => { void syncFromStorage(); };
    window.addEventListener(KLEVER_WALLET_EVENT, handler);
    // Also handle the cross-tab case (user disconnects in another tab).
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener(KLEVER_WALLET_EVENT, handler);
      window.removeEventListener('storage', handler);
    };
  }, [isExtensionAvailable, state.isConnected]);

  return {
    ...state,
    connect,
    disconnect,
    sendPayment,
    isExtensionAvailable,
  };
}
